import { useCallback, useState } from "react";
import {
  listPayables,
  ListPayablesRequest,
  Payable
} from "../../../services/api/payments/payables";

export type UseDownloadPayablesCsvArgs = {
  request: Omit<
    ListPayablesRequest,
    "page[mode]" | "page[size]" | "page[token]" | "page[resultSetId]"
  >;
  resultSetId: string | undefined;
};

const PAGE_SIZE = 100;
const MAX_PAGES = 200;

const CSV_HEADERS = [
  "Payable ID",
  "Payable Number",
  "Payee ID",
  "Payer ID",
  "Amount",
  "Currency",
  "Status",
  "Due Date",
  "Opened At",
  "Paid At",
  "Notes"
] as const;

const escapeCell = (value: unknown): string => {
  if (value === undefined || value === null) {
    return '""';
  }
  const stringified = String(value)
    .replace(/"/g, '""')
    .replace(/[\r\n]+/g, " ");
  return `"${stringified}"`;
};

const toCsvRow = (payable: Payable): string => {
  const cells: unknown[] = [
    payable.payableId,
    payable.payableNumber,
    payable.payeeId,
    payable.payerId,
    payable.amount,
    payable.currency,
    payable.status,
    payable.dueDate,
    payable.events?.openedAt,
    payable.events?.paidAt,
    payable.payableNotes
  ];
  return cells.map(escapeCell).join(",");
};

const toCsv = (payables: Payable[]): string => {
  const headerRow = CSV_HEADERS.map(escapeCell).join(",");
  const dataRows = payables.map(toCsvRow);
  return [headerRow, ...dataRows].join("\n");
};

const triggerDownload = (csv: string, filename: string): void => {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
};

export const useDownloadPayablesCsv = (args: UseDownloadPayablesCsvArgs) => {
  const [isLoading, setIsLoading] = useState(false);

  const download = useCallback(async () => {
    if (!args.resultSetId) {
      return;
    }

    setIsLoading(true);
    try {
      const collected: Payable[] = [];
      let token = "";
      let pageCount = 0;

      while (pageCount < MAX_PAGES) {
        pageCount++;
        const page = await listPayables({
          ...args.request,
          "page[mode]": "materialized",
          "page[size]": PAGE_SIZE,
          "page[resultSetId]": args.resultSetId,
          ...(token ? { "page[token]": token } : {})
        });

        collected.push(...page.items);

        if (!page.nextPageToken) {
          break;
        }
        token = page.nextPageToken;
      }

      const csv = toCsv(collected);
      const filename = `payables-${new Date().toISOString().slice(0, 10)}.csv`;
      triggerDownload(csv, filename);
    } finally {
      setIsLoading(false);
    }
  }, [args.request, args.resultSetId]);

  return {
    download,
    isLoading,
    isDisabled: !args.resultSetId
  };
};
